import React from 'react';
import clsx from 'clsx';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {makeStyles} from 'tss-react/mui';
import {SpreadSheetColHeaderCfg, SpreadSheetStyledTableProps} from './SpreadSheetStyledTableTypes';
import {SpreadSheetStyledTableUtils} from './SpreadSheetStyledTableUtils';

export const useStyle = makeStyles()((theme) => {
   const grey = 300;
   return {
      table: {tableLayout: 'fixed'},
      thead: {
         backgroundColor: 'transparent'
      },
      tBorder: {
         border: '1px solid lightgray'
      },
      tHeadCell: {
         color: theme.palette.grey[grey * 2 + 100],
         backgroundColor: theme.palette.grey[grey]
      },
      tcell: {
         paddingTop: 4,
         paddingBottom: 4,
         paddingLeft: 8,
         paddingRight: 8,
         overflow: 'hidden',
         textOverflow: 'ellipsis',
         whiteSpace: 'nowrap'
      },
      tcellNroFila: {
         paddingTop: 4,
         paddingBottom: 4,
         paddingLeft: 4,
         paddingRight: 4
      },
      tbodycell: {
         borderColor: theme.palette.grey[500]
      }
   };
});

const SpreadSheetStyledTable = <T,>({
   configCols,
   data,
   maxHeightVhMain,
   renderRow,
   getRowId,
   isLoadingData,
   noDataText
}: SpreadSheetStyledTableProps<T>) => {
   //#region estilos dinamicos
   const {classes} = useStyle();
   const maxHeaders = configCols.reduce<number>((prev, curr) => {
      if (curr.labels.length > prev) {
         return curr.labels.length;
      }
      return prev;
   }, 0);
   const topBase = 32;
   const nroDigitosDataLength =
      data.length === 0 ? 1 : Math.floor(Math.log10(Math.abs(data.length))) + 1;
   function getRowTop(rowInx: number): number {
      return topBase * rowInx;
   }
   const tnroFilaCellClass = clsx(classes.tHeadCell, classes.tBorder, classes.tcellNroFila);
   const theadCellClass = clsx(classes.tHeadCell, classes.tBorder, classes.tcell);
   const tBodyCellClass = clsx(classes.tbodycell, classes.tBorder, classes.tcell);
   const tContainerHeight = maxHeightVhMain ? `${maxHeightVhMain}vh` : '75vh';
   //#endregion

   const idCompPrefix = 'sprd-sht-styl-tbl';
   //#region ret main
   return (
      <TableContainer
         id={`${idCompPrefix}-table-container`}
         sx={{maxHeight: tContainerHeight, height: tContainerHeight}}
      >
         <Table id={`${idCompPrefix}-table`} stickyHeader size="small" className={classes.table}>
            <TableHead id={`${idCompPrefix}-thead`} className={classes.thead} sx={{top: 0}}>
               {[...Array(maxHeaders)].map((_, inxLabel) => {
                  const rowHeaderId = `${idCompPrefix}-thead-row-${inxLabel}`;
                  const rowTop = getRowTop(inxLabel);
                  const nroCellFilaStyle = {width: nroDigitosDataLength, top: rowTop};
                  const cellFilaStyle = {
                     top: rowTop
                  };
                  return (
                     <TableRow id={rowHeaderId} key={rowHeaderId}>
                        <TableCell
                           id={`${rowHeaderId}-nrocol`}
                           key={`${rowHeaderId}-nrocol`}
                           className={tnroFilaCellClass}
                           align="center"
                           style={nroCellFilaStyle}
                           title="Nro. Fila"
                        ></TableCell>
                        {configCols.map((currColConf, inx, arr) => {
                           const oculto = SpreadSheetStyledTableUtils.exePropFn(
                              currColConf.oculto,
                              null,
                              inx
                           );
                           if (oculto) {
                              return null;
                           }

                           let alinear = SpreadSheetStyledTableUtils.exePropFn(
                              currColConf.alinear,
                              null,
                              inx
                           );
                           const style = {
                              ...(SpreadSheetStyledTableUtils.exePropFn(
                                 currColConf.style,
                                 null,
                                 inx
                              ) || {}),
                              ...cellFilaStyle
                           };
                           //#region ret header cell
                           const isSimpleLabel =
                              !currColConf.labels[inxLabel] ||
                              typeof currColConf.labels[inxLabel] === 'string';

                           let finalLabel = currColConf.labels[inxLabel] as string;
                           if (!isSimpleLabel) {
                              const complexConfig = currColConf.labels[
                                 inxLabel
                              ] as SpreadSheetColHeaderCfg;
                              alinear = complexConfig.alinear || alinear;
                              finalLabel = complexConfig.text || finalLabel;
                           }
                           const finalTitle = finalLabel || currColConf.title || currColConf.id;

                           const idHeadCell = `${rowHeaderId}-${finalLabel || inx}`;

                           return (
                              <TableCell
                                 id={idHeadCell}
                                 key={idHeadCell}
                                 className={theadCellClass}
                                 align={alinear}
                                 style={{
                                    ...style,
                                    width: currColConf.width
                                 }}
                                 //className={getClassNameTHeadCell(inx, arr.length)}
                                 title={finalTitle}
                              >
                                 {finalLabel || ''}
                              </TableCell>
                           );
                           //#endregion
                        })}
                     </TableRow>
                  );
               })}
            </TableHead>

            <TableBody id={`${idCompPrefix}-table-body`}>
               {(isLoadingData || data.length === 0) && (
                  //#region body no data
                  // body cargando
                  <TableRow id={`${idCompPrefix}-tbody-row-info`}>
                     <TableCell
                        id={`${idCompPrefix}-tbody-row-info-cell`}
                        align="center"
                        colSpan={configCols.length + 1}
                     >
                        <Typography variant="h6">
                           {isLoadingData
                              ? 'Cargando datos...'
                              : noDataText || 'No existen datos para mostrar'}
                        </Typography>
                     </TableCell>
                  </TableRow>
                  //#endregion
               )}
               {!isLoadingData &&
                  data.length > 0 &&
                  //#region ret body rows
                  //#endregion
                  data.map((rowData, rowInx, arrData) => {
                     const rowDataId = `${idCompPrefix}-tbody-row-${getRowId(rowData, rowInx)}`;
                     const NroFilaCell = (
                        <TableCell
                           id={`${rowDataId}-nrocol`}
                           key={`${rowDataId}-nrocol`}
                           className={tnroFilaCellClass}
                           align="center"
                           style={{
                              width: nroDigitosDataLength,
                              maxWidth: nroDigitosDataLength
                           }}
                           //className={getClassNameTHeadCell(inx, arr.length)}
                           title="Nro. Fila"
                        >
                           {rowInx + 1}
                        </TableCell>
                     );
                     return renderRow(rowData, rowInx, {
                        classNameCell: tBodyCellClass,
                        NroFilaCell: NroFilaCell,
                        rowId: rowDataId
                     });
                  })}
            </TableBody>
         </Table>
      </TableContainer>
   );
   //#endregion
};

export default SpreadSheetStyledTable;
